import { useCallback, useState } from 'react';
import { AppModal } from '@/components/compose/app.modal';
import { Box } from '@/components/base/box';
import { AppButton } from '@/components/base/app.button';
import styled, { useTheme } from 'styled-components/native';
import { useUser } from '@/hooks/use.user';
import { NotificationPreferences, User } from '@matap/shared';
import { AppSwitch } from '@/components/base/app.switch';
import { AppTextView } from '@/components/base/app.text.view';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionSetUser } from '@/redux';
import { AppIconButton, ICONS } from '@/components/base/app.icon.button';
import { AppCardView } from '@/components/base/app.card.view';
import { AppScrollView } from '@/components/base/app.scroll.view';
import { usePatchProfile } from '@/apis/user';
import { useNavigation } from '@react-navigation/native';
import { hpVal, wpVal } from '../../utils';

const notificationTypes = [
  {
    key: 'newPatient',
    label: 'بیمار جدید',
    icon: 'person_add',
    description: 'زمانی که بیمار جدیدی به صف انتظار شما اضافه می‌شود',
  },
  {
    key: 'workTimeNear',
    label: 'نزدیک شدن به زمان پاسخگویی',
    icon: 'alarm',
    description: '۱۵ دقیقه مانده به شروع زمان پاسخگویی',
  },
  {
    key: 'workTimeStarted',
    label: 'شروع زمان پاسخگویی',
    icon: 'time',
    description: 'زمانی که زمان پاسخگویی شما آغاز می‌شود',
  },
  {
    key: 'workTimeEnded',
    label: 'پایان زمان پاسخگویی',
    icon: 'timer',
    description: 'زمانی که زمان پاسخگویی شما به پایان می‌رسد',
  },
] as { key: keyof User['doctorProfile']['notificationPreferences']; label: string; icon: keyof typeof ICONS; description: string }[];

export default function NotificationPreferencesModal() {
  const navigation = useNavigation();
  const { user } = useUser();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const initialPrefs = user.doctorProfile?.notificationPreferences || {
    newPatient: { notification: false, sms: false },
    workTimeNear: { notification: false, sms: false },
    workTimeEnded: { notification: false, sms: false },
    workTimeStarted: { notification: false, sms: false },
  };
  const [preferences, setPreferences] = useState<NotificationPreferences>(initialPrefs);
  const { mutate: updateProfile, isPending } = usePatchProfile({
    onSuccess: (data) => {
      dispatch(actionSetUser(data));
      navigation.goBack();
    },
  });

  const toggle = useCallback((type: keyof NotificationPreferences, field: 'notification' | 'sms') => {
    setPreferences((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: !prev[type][field] },
    }));
  }, []);

  const save = useCallback(() => {
    updateProfile({ body: { name: user.name, gender: user.gender, notificationPreferences: preferences } });
  }, [preferences, user, updateProfile]);

  const hasChanges = JSON.stringify(initialPrefs) !== JSON.stringify(preferences);

  return (
    <AppModal title={'تنظیمات اعلان‌ها'}>
      <Container>
        <HeaderDescription>
          <AppTextView text={'نوع اعلان‌هایی که تمایل به دریافت آن‌ها دارید را انتخاب کنید'} textColor={'grey'} variant={'primary'} />
        </HeaderDescription>
        <ScrollViewContainer contentContainerStyle={{ flexGrow: 1, alignItems: 'center', paddingTop: 10, paddingBottom: theme.hp(2) }}>
          {notificationTypes.map(({ key, label, icon, description }) => (
            <NotificationCardView key={key}>
              <CardContent>
                <CardHeader>
                  <IconContainer>
                    <AppIconButton icon={icon} size={'small'} color={theme.colors.jade} />
                  </IconContainer>
                  <FlexBox alignItems={'flex-end'}>
                    <AppTextView variant={'medium'} textColor={'blue'} text={label} />
                    <AppTextView variant={'label'} textColor={'grey'} text={description} />
                  </FlexBox>
                </CardHeader>
                <SwitchesRow>
                  <SwitchWrapper>
                    <AppTextView variant={'label'} textColor={'black_light'} text={'نوتیفیکیشن'} />
                    <ScaledSwitch value={preferences[key].notification} onValueChange={() => toggle(key, 'notification')} />
                  </SwitchWrapper>
                  <SwitchWrapper>
                    <AppTextView variant={'label'} textColor={'black_light'} text={'پیامک'} />
                    <ScaledSwitch value={preferences[key].sms} onValueChange={() => toggle(key, 'sms')} />
                  </SwitchWrapper>
                </SwitchesRow>
              </CardContent>
            </NotificationCardView>
          ))}
        </ScrollViewContainer>
        <SaveButtonContainer fullWidth>
          <SaveButton title={'ذخیره تغییرات'} loading={isPending} onPress={save} disabled={!hasChanges} />
        </SaveButtonContainer>
      </Container>
    </AppModal>
  );
}

const SaveButton = styled(AppButton)`
  border-radius: 12px;
  height: 48px;
  width: 90%;
`;

const SwitchWrapper = styled(Box)`
  align-items: center;
  gap: 6px;
`;

const SwitchesRow = styled(Box)`
  flex-direction: row;
  justify-content: space-around;
  margin-top: 12px;
  padding-top: 12px;
  border-top-width: 1px;
  border-top-color: ${({ theme }) => theme.colors.greyWhite};
`;

const CardHeader = styled(Box)`
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const IconContainer = styled(Box)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.jadeVeryLight};
  align-items: center;
  justify-content: center;
`;

const CardContent = styled(Box)`
  padding: 16px;
`;

const HeaderDescription = styled(Box)`
  align-items: flex-end;
  margin-bottom: ${hpVal(2)};
  margin-right: 20px;
`;

const Container = styled(Box)`
  flex: 1;
  align-items: flex-end;
  padding: ${hpVal(1)} ${wpVal(1)};
`;

const SaveButtonContainer = styled(Box)`
  padding-vertical: 16px;
  background-color: white;
  align-items: center;
`;

const ScrollViewContainer = styled(AppScrollView)`
  width: 100%;
`;

const NotificationCardView = styled(AppCardView)`
  width: 95%;
  align-self: center;
  margin-top: ${({ theme }) => theme.hp(1)}px;
  margin-bottom: ${({ theme }) => theme.hp(1)}px;
  border-radius: 14px;
`;

const FlexBox = styled(Box)`
  flex: 1;
  gap: 5px;
`;

const ScaledSwitch = styled(AppSwitch)`
  transform: scale(0.75);
`;
